import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload,
  Delete,
  PictureAsPdf,
  InsertDriveFile,
  OpenInNew,
} from '@mui/icons-material';
import { SlateRichTextEditor } from '../dashboard/SlateRichTextEditor';
import { newsService } from '@/services/news/newsService';
import { uploadService } from '@/services/upload/uploadService';
import { News, NewsThumbnail } from '@/constants/types';
import { toast } from 'react-hot-toast';
import { Text as SlateText, Element as SlateElement, Descendant } from 'slate';

interface CreateNewsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editNews?: News | null;
}

// Helper function to convert Slate.js to HTML
const serializeSlateToHtml = (nodes: Descendant[]): string => {
  return nodes.map((node) => serializeNode(node)).join('');
};

const serializeNode = (node: Descendant): string => {
  if (SlateText.isText(node)) {
    let text = node.text;
    if ((node as any).code) text = `<code>${text}</code>`;
    if ((node as any).bold) text = `<strong>${text}</strong>`;
    if ((node as any).italic) text = `<em>${text}</em>`;
    if ((node as any).underline) text = `<u>${text}</u>`;
    if ((node as any).strikethrough) text = `<s>${text}</s>`;
    return text;
  }

  if (SlateElement.isElement(node)) {
    const children = node.children.map((n) => serializeNode(n)).join('');
    const align = (node as any).align;
    const style = align ? ` style="text-align: ${align}"` : '';

    switch (node.type) {
      case 'block-quote':
        return `<blockquote${style}>${children}</blockquote>`;
      case 'heading-one':
        return `<h1${style}>${children}</h1>`;
      case 'heading-two':
        return `<h2${style}>${children}</h2>`;
      case 'heading-three':
        return `<h3${style}>${children}</h3>`;
      case 'heading-four':
        return `<h4${style}>${children}</h4>`;
      case 'heading-five':
        return `<h5${style}>${children}</h5>`;
      case 'heading-six':
        return `<h6${style}>${children}</h6>`;
      case 'bulleted-list':
        return `<ul>${children}</ul>`;
      case 'numbered-list':
        return `<ol>${children}</ol>`;
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link':
        return `<a href="${(node as any).url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
      case 'paragraph':
      default:
        return `<p${style}>${children}</p>`;
    }
  }

  return '';
};

const EMPTY_SLATE = JSON.stringify([
  { type: 'paragraph', children: [{ text: '' }] },
]);

const normalizeThumbnail = (raw: News['thumbnail']): NewsThumbnail | null => {
  if (!raw) return null;
  if (typeof raw === 'string') return { url: raw };
  return raw;
};

export const CreateNewsModal: React.FC<CreateNewsModalProps> = ({
  open,
  onClose,
  onSuccess,
  editNews,
}) => {
  const theme = useTheme();
  const isEditing = !!editNews;

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [slateContent, setSlateContent] = useState<string>(EMPTY_SLATE);
  const [thumbnail, setThumbnail] = useState<NewsThumbnail | null>(null);
  const [loading, setLoading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // Reset / hydrate when opening
  useEffect(() => {
    if (!open) return;
    if (editNews) {
      setTitle(editNews.title || '');
      setSummary(editNews.summary || '');
      setContent(editNews.content || '');
      setSlateContent(editNews.slateContent || EMPTY_SLATE);
      setThumbnail(normalizeThumbnail(editNews.thumbnail));
    } else {
      setTitle('');
      setSummary('');
      setContent('');
      setSlateContent(EMPTY_SLATE);
      setThumbnail(null);
    }
  }, [open, editNews]);

  const resetForm = () => {
    setTitle('');
    setSummary('');
    setContent('');
    setSlateContent(EMPTY_SLATE);
    setThumbnail(null);
  };

  const handleClose = () => {
    if (loading || thumbnailUploading) return;
    resetForm();
    onClose();
  };

  const inferAttachmentType = (mime: string): string => {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 25 * 1024 * 1024; // 25 MB
    if (file.size > MAX_SIZE) {
      toast.error('File size must be less than 25 MB');
      e.target.value = '';
      return;
    }

    try {
      setThumbnailUploading(true);
      const response = await uploadService.uploadSingle(file, { folder: 'news' });
      setThumbnail({
        mediaFileId: response.file.id,
        type: inferAttachmentType(file.type),
        url: response.file.url,
        fileName: response.file.fileName || file.name,
        contentType: file.type,
        size: file.size,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload file');
    } finally {
      setThumbnailUploading(false);
      // reset input value so the same file can be re-selected
      e.target.value = '';
    }
  };

  const removeThumbnail = () => setThumbnail(null);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!content.trim() || content === '<p></p>') {
      toast.error('Content is required');
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        title: title.trim(),
        summary: summary.trim() || undefined,
        content,
        slateContent,
        thumbnail: thumbnail || undefined,
      };

      if (isEditing && editNews) {
        payload.isActive = editNews.isActive ?? true;
        await newsService.updateNews(editNews._id, payload);
        toast.success('News updated successfully');
      } else {
        await newsService.createNews(payload);
        toast.success('News created successfully');
      }

      onSuccess();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to save news:', error);
      toast.error(isEditing ? 'Failed to update news' : 'Failed to create news');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditing ? 'Edit News' : 'Create News'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3} pt={1}>
          <TextField
            label="Title"
            placeholder="Enter news title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
          />

          {/* <TextField
            label="Summary (optional)"
            placeholder="Short summary shown in the list"
            fullWidth
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            variant="outlined"
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: 280 }}
            helperText={`${summary.length}/280 characters`}
          /> */}

          {/* Attachment (image or document) */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Attachment (optional — image or document)
            </Typography>
            {thumbnail?.url ? (
              thumbnail.type === 'image' || (thumbnail.contentType || '').startsWith('image/') ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 200,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    component="img"
                    src={thumbnail.url}
                    alt={thumbnail.fileName || 'Attachment'}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    onClick={removeThumbnail}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                  }}
                >
                  {(thumbnail.contentType || '').includes('pdf') ? (
                    <PictureAsPdf color="error" sx={{ fontSize: 32, flexShrink: 0 }} />
                  ) : (
                    <InsertDriveFile color="primary" sx={{ fontSize: 32, flexShrink: 0 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {thumbnail.fileName || 'Document'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[thumbnail.contentType, formatFileSize(thumbnail.size)].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={thumbnail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                  <IconButton onClick={removeThumbnail} size="small" color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              )
            ) : (
              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={thumbnailUploading ? <CircularProgress size={18} /> : <CloudUpload />}
                disabled={thumbnailUploading}
                sx={{
                  height: 100,
                  borderStyle: 'dashed',
                  textTransform: 'none',
                  flexDirection: 'column',
                  gap: 0.25,
                }}
              >
                <Box>{thumbnailUploading ? 'Uploading...' : 'Upload Image or Document'}</Box>
                {!thumbnailUploading && (
                  <Typography variant="caption" color="text.secondary">
                    Images, PDF, DOC, DOCX, XLS — max 25 MB
                  </Typography>
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                  onChange={handleThumbnailUpload}
                />
              </Button>
            )}
          </Box>

          <Box>
            <SlateRichTextEditor
              value={slateContent}
              onChange={(value) => {
                setSlateContent(value);
                try {
                  const nodes = JSON.parse(value);
                  setContent(serializeSlateToHtml(nodes));
                } catch (err) {
                  console.error('Failed to parse slate nodes:', err);
                }
              }}
              placeholder="Write news description here..."
              title='Description'
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button variant='outlined' onClick={handleClose} disabled={loading || thumbnailUploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || thumbnailUploading}
          sx={{
            bgcolor: '#0a66c2',
            '&:hover': { bgcolor: '#004182' },
            px: 3,
            textTransform: 'none',
          }}
        >
          {loading
            ? (isEditing ? 'Updating...' : 'Creating...')
            : (isEditing ? 'Update News' : 'Create News')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
